// Types derived from the session events format (as opposed to the legacy logs format).
// Keeping these separate makes it easier to migrate to a fully event-based model.

/**
 * abort signals that the agent's current operation was cancelled
 */
export type AbortMessage = {
  type: 'abort'
  id: string
  timestamp: string
  agentId?: string
  data: {
    reason: string
  }
}

export function isAbortMessage(logEntry: unknown): logEntry is AbortMessage {
  return typeof logEntry === 'object' && logEntry !== null && 'type' in logEntry && logEntry.type === 'abort'
}

export type SessionModeChangedMessage = {
  type: 'session.mode_changed'
  id: string
  timestamp: string
  data: {
    previousMode: string
    newMode: string
  }
}

export function isSessionModeChangedMessage(logEntry: unknown): logEntry is SessionModeChangedMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'session.mode_changed' &&
    'data' in logEntry &&
    typeof (logEntry as SessionModeChangedMessage).data?.newMode === 'string'
  )
}

export type SessionResumeMessage = {
  type: 'session.resume'
  id: string
  timestamp: string
  data: {
    resumeTime: string
    eventCount: number
  }
}

export function isSessionResumeMessage(logEntry: unknown): logEntry is SessionResumeMessage {
  return typeof logEntry === 'object' && logEntry !== null && 'type' in logEntry && logEntry.type === 'session.resume'
}

/**
 * exit_plan_mode.requested is emitted when the agent calls exit_plan_mode and
 * the session needs plan approval from a client. Contains richer data than
 * the CompletionChunk tool call (includes planContent).
 */
export type ExitPlanModeRequestedMessage = {
  type: 'exit_plan_mode.requested'
  id: string
  timestamp: string
  ephemeral: true
  data: {
    requestId: string
    summary: string
    planContent?: string
    actions?: string[]
    recommendedAction?: string
    toolCallId: string
  }
}

export function isExitPlanModeRequestedMessage(logEntry: unknown): logEntry is ExitPlanModeRequestedMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'exit_plan_mode.requested' &&
    'data' in logEntry &&
    typeof (logEntry as ExitPlanModeRequestedMessage).data?.toolCallId === 'string'
  )
}

/**
 * exit_plan_mode.completed is emitted when a pending exit plan mode request
 * has been resolved by a client. Contains the user's approval decision and optional feedback.
 */
export type ExitPlanModeCompletedMessage = {
  type: 'exit_plan_mode.completed'
  id: string
  timestamp: string
  ephemeral: true
  data: {
    requestId: string
    approved?: boolean
    selectedAction?: string
    autoApproveEdits?: boolean
    feedback?: string
  }
}

export function isExitPlanModeCompletedMessage(logEntry: unknown): logEntry is ExitPlanModeCompletedMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'exit_plan_mode.completed' &&
    'data' in logEntry &&
    typeof (logEntry as ExitPlanModeCompletedMessage).data?.requestId === 'string'
  )
}

/**
 * session.remote_steerable_changed is emitted when a CLI session's remote
 * steering capability changes. Used to show an inline notification in session logs.
 */
export type RemoteSteerableChangedMessage = {
  type: 'session.remote_steerable_changed'
  id: string
  timestamp: string
  data: {
    remoteSteerable: boolean
  }
}

export function isRemoteSteerableChangedMessage(logEntry: unknown): logEntry is RemoteSteerableChangedMessage {
  return (
    typeof logEntry === 'object' &&
    logEntry !== null &&
    'type' in logEntry &&
    logEntry.type === 'session.remote_steerable_changed'
  )
}
